import * as Actions from '../actions/categoryActions';
import Category from '../../entities/category';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CategoryAction = { 
    type: string; 
    category: Category | null; 
    categoryId: string | Category; 
    partial: boolean; 
    lastFetch: number; 
    categories: Category[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Category> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Category[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Category> | null, firmId: string, userId: string, updates?: Category[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'category', 'categories', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Category>} data
 * @param {Category[]} [updates]
 * @returns {ArrayMap<Category>}
 */
function updateStorage(data: ArrayMap<Category>, updates?: Category[]): ArrayMap<Category> {
	if (logging) console.debug('⮂ categoryReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Category[]} [updates]
 * @returns {ArrayMap<Category>}
 */
function updateStorageNoReturn(updates?: Category[]): void {
	if (logging) console.debug('⮂ categoryReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Category[]} [data]
 * @returns {ArrayMap<Category>}
 */
function createState(data?: Category[] | ArrayMap<Category>): ArrayMap<Category> {
	if (!data) return ArrayMapRegistry.clone<Category>('category', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Category>('category', data.map(e => e));
    else return ArrayMapRegistry.clone<Category>('category', data);
}

function categories(state: ArrayMap<Category>, action: CategoryAction): ArrayMap<Category> {
	if (!state) state = createState();

	let st: ArrayMap<Category>;

	switch (action.type) {
		// action.category - the new value of category with id action.id
		// action.categoryId - id of the target, used if new value is null
		case Actions.CATEGORY_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ categoryReducer: FETCH_ONE_SUCCESS', action);

			if (action.category === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.categoryId)) {
				st = state.clone();
				st.set(action.category);
				// state.clear();

				updateStorageNoReturn([action.category]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.categories
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.categories
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.categories
			 !partial, !stateEmpty, fetchEmpty	-> D return categories in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge categories in state newer than those in action.categories
		 */
		case Actions.CATEGORY_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.categories.length === 0;
			const fetched = action.categories.map(category => new Category({...category, persisted: true}));

			if (logging) console.debug(`⮂ categoryReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} categories with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ categoryReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ categoryReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Category> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Category[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(category => {
					const current = st.get(category.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && category.status === 'D') {
							// Remove it from state
							toUpdate.push(category);
							st.delete(category);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (category.addedOn > current.addedOn) {
								toUpdate.push(category);
								st.set(category);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (category.status !== 'D') {
							toUpdate.push(category);
							st.set(category);
						}
						else if (logging) console.debug('⮂ categoryReducer: partialMerge: skipping deleted category not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ categoryReducer: empty: categories', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ categoryReducer: fullMerge: categories', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCategory => {
                    // If this category has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCategory.persisted) {
                        const fetchedCategory = st.get(localCategory.id);
                        if (!fetchedCategory || localCategory.addedOn > fetchedCategory.addedOn) st.set(localCategory);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.CATEGORY_FETCH_ONE_FAILURE:
			console.error('CATEGORY_FETCH_ONE_FAILURE', action.categoryId, action.err);

			// No update to storage required
			return state;

		case Actions.CATEGORY_FETCH_FAILURE:
			console.error('CATEGORY_FETCH_FAILURE', action.categoryId, action.err);

			// No update to storage required
			return state;

		case Actions.CATEGORY_RESTORE: {
		    let category = new Category({...action.category});
            st = state.clone();

            const existing = st.get(category.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (category.addedOn >= existing.addedOn) {
                    st.set(category);
                    return updateStorage(st, [category]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (category.status === 'D') return state;

                st.set(category);
                return updateStorage(st, [category]);
            }
        }
            
		case Actions.CATEGORY_SAVE: {
		    // console.debug('⮂ CATEGORY_SAVE reducer', action.category?.id);
		    
			// clone the category
			let category = new Category({...action.category});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new category in the state
			// If this is a create, add to the queue
			const _category = st.get(category.id);

			if (!_category) st.set(category);
			else {
				if (category.addedOn >= _category.addedOn) st.set(category);
			}

			return updateStorage(st, [category]);
		}

		case Actions.CATEGORY_SAVE_MANY: {
		    if (logging) console.debug('⮂ CATEGORY_SAVE_MANY reducer', action.categories.map(category => category.id).join(','));
		    
			// clone the categories
			let toProcess = action.categories.map(category => new Category(category));

			st = state.clone();
			// state.clear();

			const updates: Category[] = [];

			toProcess.forEach(category => {
				// Add or update the new category in the state
				// If this is a create, add to the queue
				const existing = st.get(category.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (category.status !== 'D') {
						st.push(category);
						updates.push(category);
					}
					else if (logging) console.debug('⮂ CATEGORY_SAVE_MANY: skipping deleted category not in state');
				}
				else {
					if (category.addedOn >= existing.addedOn) {
						st.set(category);
						updates.push(category);
					}
					else if (logging) console.debug('⮂ Skipping, existing category had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CATEGORY_REMOVE: {
			// clone the category
			const category = new Category({...action.category});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(category);

			return updateStorage(st, [category]);
		}

		case Actions.CATEGORY_RESTORE_FAILURE:
		case Actions.CATEGORY_SAVE_FAILURE: {
		    console.warn('CATEGORY_SAVE_FAILURE reducer', action.category?.id);
		    if (!action.category?.id) {
				console.warn('CATEGORY_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const category = ensure(Category, action.category);
			if (!category) {
				console.error('Could not find category after call to save');
				return state;
			}

			st = state.clone();

			const _categoryRef = state.get(category.id);

			if (_categoryRef && (_categoryRef.addedOn === category.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _category = new Category({..._categoryRef});
				_category.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_category.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('category', _category).then(r => {
						if (r.isErr()) console.error(`⮂ categoryReducer: failed to save category with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ categoryReducer: saved category with fatalError info');
					}).catch(err => console.error(`⮂ categoryReducer: failed to save category with fatalError info: ${err.message}`));

				st.set(_category);
			}

			return updateStorage(st);
		}

		case Actions.CATEGORY_SAVE_MANY_FAILURE: {
		    console.warn('CATEGORY_SAVE_MANY_FAILURE reducer', action.categories.map(category => category.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.CATEGORY_RESTORE_SUCCESS:
		case Actions.CATEGORY_SAVE_SUCCESS: {
			if (!action.category?.id) {
				console.warn('CATEGORY_SAVE_SUCCESS reducer');
                return state;
			}

			const categoryRef = state.get(action.category?.id);
			if (!categoryRef) {
				console.warn('Could not find category after call to save');
                return state;
			}

			st = state.clone();

			if (action.category.addedOn >= categoryRef.addedOn) {
				st.set(action.category);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const category = new Category({...categoryRef, persisted: true});
				st.set(category);
			}

			return updateStorage(st, [action.category]);
		}

		case Actions.CATEGORY_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ CATEGORY_SAVE_MANY_SUCCESS reducer', action.categories.map(category => category.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const categories: Category[] = [];

			action.categories.forEach(e => {
				const categoryRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!categoryRef) console.warn('Could not find category after call to save');
				else if (e.addedOn >= categoryRef.addedOn) {
				    st.set(e);
				    categories.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const category = new Category({...categoryRef, persisted: true});
					st.set(category);
					categories.push(category);
				}
			});

			return updateStorage(st, categories);
		}

		case Actions.CATEGORY_REMOVE_FAILURE: {
		    console.warn('CATEGORY_REMOVE_FAILURE action', action.category?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.CATEGORY_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.CATEGORY_RESTORE_MANY: {
			if (logging) console.debug('⮂ CATEGORY_RESTORE_MANY reducer', action.categories.map(category => category.id).join(','));

			const toProcess = action.categories.map(category => new Category({...category}));
			st = state.clone();

			const updates: Category[] = [];

			toProcess.forEach(category => {
				const existing = st.get(category.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (category.addedOn >= existing.addedOn) {
						st.set(category);
						updates.push(category);
					}
					else if (logging) console.debug('⮂ CATEGORY_RESTORE_MANY: skipping older version of category');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (category.status !== 'D') {
						st.set(category);
						updates.push(category);
					}
					else if (logging) console.debug('⮂ CATEGORY_RESTORE_MANY: skipping deleted category not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.CATEGORY_REMOVE_MANY: {
			if (logging) console.debug('⮂ CATEGORY_REMOVE_MANY reducer', action.categories.map(category => category.id).join(','));

			const toProcess = action.categories.map(category => new Category({...category}));
			st = state.clone();

			toProcess.forEach(category => {
				st.delete(category);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default categories;